import { ActivityIndicator, RadioButton, Text } from "react-native-paper";
import { FlatList, View } from "react-native";
import { FormProvider, useForm, FieldPath, Control, FieldPathByValue } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { isEmpty, isEqual, mapValues, pick, trim } from "lodash";

import { FormData, defaultValues as initialValues, AddressFields, defaultAddressFields } from "../AccountScreen/types";
import { ScreenProps } from "./types";
import { Address, AddressType } from "../types";
import {
  ScreenContainer,
  ScrollableContainer,
  KeyboardAvoidingView,
  useErrorScroll,
  ActionsContainer,
  Button,
} from "../../components";
import AddressAutocompleteInput from "../../components/AddressAutoComplete";
import CountryDropdown, {
  countryCodeToLabel,
  countryLabelToCode,
  isUnitedStates,
} from "../../components/AddressAutoComplete/CountryDropdown";
import TextInput from "../AccountScreen/TextInput";
import { colors } from "../../../theme";
import UnsavedChangesModal from "../../components/UnsavedChangesModal";
import { CURRENT_PERSON_FRAGMENT } from "../fragments";
import { ACCOUNT_EDIT_QUERY, Query } from "../AccountScreen/EditScreen";
import useSnackBar from "../../hooks/useSnackBar";
import HomeMobileNumberInputs from "./HomeMobileNumberInputs";

export const navigationTitle = "Contact Information";

export const MUTATION = gql`
  mutation updatePerson($input: CRMUpdatePersonInput!) {
    updatePerson(input: $input) {
      person {
        ...CurrentPersonFragment
      }
      message
      success
    }
  }
  ${CURRENT_PERSON_FRAGMENT}
`;

export const isVerified = (
  addressObj: Partial<AddressFields>,
  autoCompleteFields: Record<string, string>,
  prevAddress?: Partial<Address>
) => {
  const isPreviousVerified = prevAddress?.isVerified;
  const keys = ["address", "city", "state", "postalCode"];
  if (!isEmpty(autoCompleteFields)) {
    const autoCompleteValues = pick(autoCompleteFields, keys);
    const userValues = pick(addressObj, keys);

    return isEqual(autoCompleteValues, userValues);
  }

  if (isPreviousVerified) {
    const prevValues = pick(prevAddress, keys);
    const userValues = pick(addressObj, keys);

    return isEqual(prevValues, userValues);
  }

  return false;
};

const prefix =
  (type: AddressType) =>
  (field: keyof AddressFields): FieldPathByValue<FormData, string> =>
    `${type}.${field}`;

function addressFieldPaths(type: AddressType) {
  let field: keyof AddressFields;
  let fields: FieldPathByValue<FormData, string>[] = [];
  let name = prefix(type);

  for (field in defaultAddressFields) {
    fields.push(name(field));
  }

  return fields;
}

function AddressFormFields({
  name: name$,
  control,
  focus: focus$,
  scrollToField,
  handleSuggest,
}: {
  name: AddressType;
  control: Control<FormData>;
  focus(field: FieldPath<FormData>): () => void;
  scrollToField(field: FieldPath<FormData>): void;
  handleSuggest(data: AddressFields): void;
}) {
  const name = prefix(name$);
  const focus = (field: keyof AddressFields) => focus$(name(field));

  const clearFields = addressFieldPaths(name$).filter((field) => field !== name("country"));

  return (
    <>
      <CountryDropdown
        control={control}
        name={name("country")}
        rules={{ required: "required" }}
        onDismiss={focus("address")}
        clearFields={clearFields}
      />
      <View className="mt-2">
        <AddressAutocompleteInput
          autoComplete="postal-address"
          control={control}
          label="Street Address"
          name={name("address")}
          onSubmitEditing={focus("address2")}
          rules={{ required: "required" }}
          textContentType="streetAddressLine1"
          onFocus={() => scrollToField(name("address"))}
          countryField={name("country")}
          onSuggest={handleSuggest}
          mapField={name}
        />
      </View>

      <TextInput
        autoComplete="postal-address-extended"
        className="mt-2"
        control={control}
        label="Apt. or Suite #"
        name={name("address2")}
        onSubmitEditing={focus("city")}
        textContentType="streetAddressLine2"
      />

      <TextInput
        autoComplete="postal-address-locality"
        className="mt-2"
        control={control}
        label="City"
        name={name("city")}
        rules={{ required: "required" }}
        onSubmitEditing={focus("state")}
        textContentType="addressCity"
      />
      <View className="flex-row flex-wrap mt-2">
        <View className="flex-1">
          <TextInput
            control={control}
            label="State/Province"
            name={name("state")}
            rules={{
              validate: {
                required: (value, { country }) => (isUnitedStates(country) && !value ? "required" : true),
              },
            }}
            onSubmitEditing={focus("postalCode")}
            textContentType="addressState"
          />
        </View>

        <View className="flex-1">
          <TextInput
            autoComplete="postal-address-extended-postal-code"
            className="ml-2.5 grow-0"
            control={control}
            keyboardType="number-pad"
            label="Zip/Postal Code"
            name={name("postalCode")}
            rules={{
              validate: {
                required: (value, { country }) => validatePostalCode(value, country),
              },
            }}
            textContentType="postalCode"
            errorClassName="pl-3"
          />
        </View>
      </View>
    </>
  );
}

function getAddress(
  formData: FormData,
  data: Query,
  type: "home" | "secondary",
  addressFields?: AddressFields
): Omit<Address, "id" | "addressable"> {
  const { addresses } = data.currentPerson;
  const prevAddress = addresses.find((a) => a.addressType === type);

  const { postalCode, country, ...rest } = formData[type];
  const { city, state, address: address1, address2 } = mapValues(rest, trim);

  const addressTrim = {
    address: address1,
    address2,
    city,
    state,
    postalCode,
  };

  const prev = {
    address: prevAddress?.address1,
    ...pick(prevAddress, ["city", "state", "postalCode", "isVerified"]),
  };

  return {
    address1,
    address2,
    city,
    state,
    postalCode,
    isVerified: isVerified(addressTrim, mapValues(addressFields, trim), prev),
    countryCode: countryLabelToCode(country),
    addressType: type,
  };
}

const getHomeAddress = (addr: FormData, data: Query, fields?: AddressFields) => getAddress(addr, data, "home", fields);

const getSecondaryAddress = (addr: FormData, data: Query, fields?: AddressFields) =>
  getAddress(addr, data, "secondary", fields);

export const validatePostalCode = (value: string, country: string) => {
  if (isUnitedStates(country)) {
    if (!value) return "required";
    return /^\d{5}(?:[-\s]\d{4})?$/.test(value) ? true : "Value must be valid.";
  }

  return /^[A-Z0-9\s\-]{3,10}$/.test(value) ? true : "Value must be valid.";
};

interface Props extends ScreenProps<"AccountEditContactInformation"> {
  data: Query;
}

function AccountEditContactForm({ navigation, data }: Props) {
  const { addresses, phoneNumbers } = data.currentPerson;
  const homeAddr = addresses.find((a) => a.addressType === "home");
  const secondaryAddr = addresses.find((a) => a.addressType === "secondary");

  const homePhone = phoneNumbers.find(({ phoneType }) => phoneType === "home");
  const mobilePhone = phoneNumbers.find(({ phoneType }) => phoneType === "mobile");

  const defaultValues = {
    ...initialValues,

    ...(homeAddr && {
      home: {
        address: homeAddr?.address1,
        address2: homeAddr?.address2,
        city: homeAddr?.city,
        state: homeAddr?.state,
        postalCode: homeAddr?.postalCode,
        country: countryCodeToLabel(homeAddr?.countryCode),
      },
    }),

    ...(secondaryAddr && {
      secondary: {
        address: secondaryAddr?.address1,
        address2: secondaryAddr?.address2,
        city: secondaryAddr?.city,
        state: secondaryAddr?.state,
        postalCode: secondaryAddr?.postalCode,
        country: countryCodeToLabel(secondaryAddr?.countryCode),
      },
    }),

    ...(homePhone && { homePhone: homePhone.phoneNumber }),
    ...(mobilePhone && { mobilePhone: mobilePhone.phoneNumber }),
    ...(homePhone && { homePhoneCountryCode: homePhone.phoneCountry }),
    ...(mobilePhone && { mobilePhoneCountryCode: mobilePhone.phoneCountry }),
  };

  const methods = useForm<FormData>({ mode: "onChange", defaultValues });
  const flatListRef = useRef<FlatList>(null);
  const {
    control,
    handleSubmit,
    setFocus,
    setValue,
    trigger,
    formState: { isValid, isSubmitting, isDirty },
  } = methods;
  const [secondaryAddress, setSecondaryAddress] = useState<"yes" | "no">(secondaryAddr ? "yes" : "no");

  const [homeAddressFields, setHomeAddressFields] = useState<AddressFields | undefined>();
  const [secondaryAddressFields, setSecondaryAddressFields] = useState<AddressFields | undefined>();

  const handleHomeSuggest = (data: AddressFields) => setHomeAddressFields(data);
  const handleSecondarySuggest = (data: AddressFields) => setSecondaryAddressFields(data);

  const [discardChanges, setDiscardChanges] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const showSnackBar = useSnackBar();

  const [sendMutation, { loading }] = useMutation(MUTATION);

  useEffect(() => {
    if (secondaryAddress === "no") {
      setValue("secondary", initialValues.secondary, { shouldValidate: false });
    }
    trigger();
  }, [secondaryAddress]);

  useEffect(() => {
    if (discardChanges && navigation.canGoBack()) {
      return navigation.goBack();
    }
    const unsubscribe = navigation.addListener("beforeRemove", (event) => {
      if (isDirty) {
        event.preventDefault();
        setShowUnsavedChangesModal(true);
      }
    });

    return unsubscribe;
  }, [navigation, isDirty, discardChanges]);

  const discardAndExit = () => setDiscardChanges(true);
  const navigateToAccountEdit = () => navigation.getParent()?.navigate("Account", { screen: "AccountEdit" });

  const { Divider, onSubmitError, scrollToField } = useErrorScroll({
    defaultValues,
    flatListRef,
  });

  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setFocus("home.address");
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [setFocus]);

  async function onSubmit(values: FormData) {
    const homeAddress = getHomeAddress(values, data, homeAddressFields);
    const secAddress = getSecondaryAddress(values, data, secondaryAddressFields);
    const inputAddresses = [homeAddress, secAddress].filter((v) => !isEmpty(v));
    const inputPhoneNumbers = [
      { phoneNumber: values.homePhone, phoneType: "home" },
      { phoneNumber: values.mobilePhone, phoneType: "mobile" },
    ];

    discardAndExit();

    const input = { addresses: inputAddresses, phoneNumbers: inputPhoneNumbers };
    const {
      data: {
        updatePerson: { success },
      },
    } = await sendMutation({ variables: { input } });

    if (success) {
      showSnackBar({ message: "Changes Saved", duration: 3000, icon: "check-circle" });
      navigateToAccountEdit();
    }
  }

  const focus = (field: FieldPath<FormData>) => () => setFocus(field);

  return (
    <ScreenContainer testID="AccountEditContactScreen" navigationTitle={navigationTitle} goBack={navigateToAccountEdit}>
      <UnsavedChangesModal
        {...{
          mutationLoading: loading,
          showUnsavedChangesModal,
          setShowUnsavedChangesModal,
          discardAndExit,
          handleSubmit: () => setShowUnsavedChangesModal(false),
          title: "Exit and discard changes?",
          message: "Changes to this form will be lost.",
          buttonLabel: "Continue",
        }}
      />
      <FormProvider {...methods}>
        <KeyboardAvoidingView>
          <ScrollableContainer ref={flatListRef} keyboardShouldPersistTaps="always">
            <View className="m-6">
              <Divider hidden fields={addressFieldPaths("home")} />
              <Text className="text-regular-tight font-700 pb-4 text-ink-darkest">Home Address</Text>
              <AddressFormFields
                name="home"
                control={control}
                focus={focus}
                scrollToField={scrollToField}
                handleSuggest={handleHomeSuggest}
              />
              <Divider hidden fields={addressFieldPaths("secondary")} />
              <Text className="text-regular-tight font-700 text-ink-darkest mt-8">Secondary Address</Text>
              <Text className="text-small-normal font-400 text-ink-dark mt-2">
                Do you have a secondary or seasonal address that you would like to include?
              </Text>

              <View className="flex flex-row mt-3">
                <View className="flex flex-row items-center">
                  <RadioButton.Android
                    value={secondaryAddress}
                    status={secondaryAddress === "yes" ? "checked" : "unchecked"}
                    onPress={() => setSecondaryAddress("yes")}
                    uncheckedColor={colors.sky.DEFAULT}
                    testID="secondary-address-yes-btn"
                  />
                  <Text className="font-400 text-small-normal text-black mr-6">Yes</Text>
                </View>

                <View className="flex flex-row items-center">
                  <RadioButton.Android
                    value={secondaryAddress}
                    status={secondaryAddress === "no" ? "checked" : "unchecked"}
                    onPress={() => setSecondaryAddress("no")}
                    uncheckedColor={colors.sky.DEFAULT}
                  />
                  <Text className="font-400 text-small-normal text-black">No</Text>
                </View>
              </View>

              {secondaryAddress === "yes" && (
                <View className="mt-4">
                  <AddressFormFields
                    name="secondary"
                    control={control}
                    focus={focus}
                    scrollToField={scrollToField}
                    handleSuggest={handleSecondarySuggest}
                  />
                </View>
              )}
            </View>

            <Divider fields={["homePhone", "mobilePhone"]} />
            <View className="mx-6 mt-6 mb-8">
              <Text className="text-regular-tight font-700 text-ink-darkest">Phone Numbers</Text>
              <Text className="text-small-normal font-400 text-ink-dark mt-2">At least one phone number is required.</Text>
              <HomeMobileNumberInputs />
            </View>

            <ActionsContainer errorAlert errorMessage={errorMessage} onCloseErrorMessage={setErrorMessage}>
              <Button
                loading={loading}
                disabled={isSubmitting}
                isValid={isValid}
                mode="contained"
                onPress={handleSubmit(onSubmit, onSubmitError)}
                testID="submit"
              >
                Submit
              </Button>
            </ActionsContainer>
          </ScrollableContainer>
        </KeyboardAvoidingView>
      </FormProvider>
    </ScreenContainer>
  );
}

export default function AccountEditContactScreen(props: ScreenProps<"AccountEditContactInformation">) {
  const { loading, data } = useQuery<Query>(ACCOUNT_EDIT_QUERY);

  if (loading || !data) {
    return <ActivityIndicator className="flex-1 items-center justify-center bg-white" />;
  }

  return <AccountEditContactForm {...props} data={data} />;
}
